import { confirmDialog, alertDialog } from '@/lib/dialogs';
import { eventBusHub } from '@/lib/eventBusHub';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Trash2, Check, X, Minus, Plus, RefreshCw, Folder, Settings, CalendarDays, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/widgets/DataTableWidget';
import { ObjectCard, type CardField, type CardTemplateDef } from '@/components/ui/ObjectCard';
import { listTagsForSubjects, type TagInfo } from '@/lib/tags';
import { logErrorToJournal } from '@/lib/errors';
import { onDataChanged } from '@/lib/dataEvents';
import { useContextChatStore } from '@/lib/contextChat';
import { CardGrid, cardNarrowClass } from '@/components/ui/CardGrid';
import { ListToolbar } from '@/components/ui/ListToolbar';
import { GroupedList } from '@/components/autoforms/GroupedList';
import { ListSettingsDialog } from '@/components/autoforms/ListSettingsDialog';
import { PeriodFilterDialog } from '@/components/autoforms/PeriodFilterDialog';
import { PrintListDialog } from '@/components/autoforms/PrintListDialog';
import { settingsStore } from '@/lib/settingsStore';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { isElementVisible, loadVisibilityOptions } from '@/lib/visibility';
import { viewForDevice, parseViewSettings } from '@/lib/viewSettings';
import { formatValue, loadConfigFormats, subscribeConfigFormats } from '@/lib/format';
import { subscribeVisibility } from '@/lib/visibility';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { getSDK } from '@/store/sdkStore';
import { useAuthStore } from '@/store/authStore';
import { styleCss } from '@/components/ui/StyleBadge';
import { applyFormats, type CondFormat, type CondContext } from '@/lib/conditionalFormat';
import { dataService } from '@/lib/dataService';
import type { ListFilter } from '@/types/formProperties';
import { createLogger } from '@/lib/logger';
import { CopyButton } from '@/components/ui/CopyButton';

const log = createLogger('AutoListForm');

export interface AutoListFormProps {
  objectType: string;
  objectName: string;
  onRowDoubleClick?: (row: Record<string, unknown>) => void;
  /** Включённые стандартные команды (undefined = все): create, refresh, delete, search, filter */
  commands?: string[];
  /** Отборы динамического списка (как в 1С) */
  filters?: ListFilter[];
  /** Серверные отборы (SSoT — сервер, точное равенство; query-отборы сайтов §2):
   *  передаются в ListTableData.filters ДО выборки (не клиентский applyFilters). */
  serverFilters?: ListFilter[];
  /** Колбэк команды «Создать» */
  onCreate?: () => void;
  /** Колбэк команды «Удалить» (ids) */
  onDelete?: (ids: string[]) => void;
  /** Одиночный клик по строке (для форм выбора) */
  onSelect?: (row: Record<string, unknown>) => void;
  /** Показывать панель команд (по умолчанию да) */
  showToolbar?: boolean;
  /** Ограничить колонки таблицы (для кастомных форм списка: поля макета = колонки) */
  visibleKeys?: string[];
  /** Переопределить физическую таблицу (для кастомных форм списка с другим источником) */
  table?: string;
  /** Режим выбора (как в 1С): '' (любое) | 'items' (элементы) | 'groups' (группы) | 'both' (любое) */
  selectionMode?: string;
  /** Вид динамического списка (как в 1С): 'list' (список) | 'tree' (дерево) | 'flat' (плоский) | 'cards' (карточки) */
  viewMode?: 'list' | 'tree' | 'flat' | 'cards';
  /** Одиночный клик по строке ОТКРЫВАЕТ объект (onRowDoubleClick), удержание — выделение */
  singleClickOpen?: boolean;
  /** «Виды» объекта метаданных (десктоп/мобильный) — единый механизм:
      применяется, если форма не задала явный вид */
  objectViewSettings?: { view?: string; cardTemplate?: string };
  /** Права текущего пользователя (как в 1С): если заданы — скрываются
   * недоступные команды. Пусто = сервер сам решит (прав нет по умолчанию). */
  rights?: { canRead?: boolean; canCreate?: boolean; canUpdate?: boolean; canDelete?: boolean; canViewMarked?: boolean } | null;
  /** Почтовый ящик задач (SSoT сервера): папка фильтруется ListTasks */
  taskFolder?: string;
  /** ГОЛОСОВОЙ ПОИСК (свойство списков всех видов): микрофон у поля поиска */
  voiceSearch?: boolean;
  /** ГРУППИРОВКА: режим ('' | sections | kanban | chips | cards) и поля группировки
   *  (CSV: «status_code,assignee_id» — порядок = вложенность для «карточек-групп»).
   *  Работает для ЛЮБЫХ форм списков: заявки/задачи/справочники/документы/регистры. */
  groupMode?: string;
  groupBy?: string;
  /** Панель папок (если передана — показывается «Сохранить отбор как папку») */
  foldersPanel?: boolean;
  /** Отбор списка ЗАВЕДОМО пуст (незаполненная привязка @objectId/@param/@context —
   *  резолв lib/dlFilters): данные НЕ грузятся («без отбора» показывал бы все
   *  записи базы), список пустой по построению. */
  forceEmpty?: boolean;
}

/** Применяет отборы динамического списка к строкам (как в 1С). */
/** Локальный поиск по всем колонкам (общий для list/tree/flat/cards). */
export function applySearch(rows: Array<Record<string, unknown>>, text: string): Array<Record<string, unknown>> {
  const q = text.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((row) =>
    Object.entries(row).some(([k, v]) => {
      if (k.endsWith('_presentation') || k === 'id') return false;
      return String(v ?? '').toLowerCase().includes(q);
    })
  );
}

export function applyFilters(rows: Array<Record<string, unknown>>, filters?: ListFilter[]): Array<Record<string, unknown>> {
  if (!filters || filters.length === 0) return rows;
  return rows.filter((row) => filters.every((f) => {
    const v = row[f.field];
    const sv = v === null || v === undefined ? '' : String(v);
    switch (f.condition) {
      case '=': return sv === (f.value ?? '');
      case '!=': return sv !== (f.value ?? '');
      case '>': case '<': case '>=': case '<=': {
        // Числа — числовое сравнение; ДАТЫ (ISO-строки) — лексикографически:
        // иначе Number('2026-09-30') = NaN и отбор ПО ПЕРИОДУ отбрасывал всё.
        const nv = Number(sv);
        const nf = Number(f.value ?? 0);
        if (Number.isNaN(nv) || Number.isNaN(nf)) {
          const a = sv;
          const b = f.value ?? '';
          if (f.condition === '>') return a > b;
          if (f.condition === '<') return a < b;
          if (f.condition === '>=') return a >= b;
          return a <= b;
        }
        if (f.condition === '>') return nv > nf;
        if (f.condition === '<') return nv < nf;
        if (f.condition === '>=') return nv >= nf;
        return nv <= nf;
      }
      case 'like': return sv.toLowerCase().includes((f.value ?? '').toLowerCase());
      case 'is_null': return v === null || v === undefined || sv === '';
      case 'is_not_null': return v !== null && v !== undefined && sv !== '';
      default: return true;
    }
  }));
}

/**
 * Ячейка ссылки: представление из серверной колонки {col}_presentation.
 * Детерминированно: пустая ссылка → «—»; заданная без представления (висячая) → «—».
 * Никогда не показываем сырой UUID.
 */
export function RefCell({ id, presentation }: { table?: string; id: unknown; presentation?: string }) {
  const value = String(id ?? '');
  if (!value) return <span className="text-muted-foreground">—</span>;
  if (presentation) return <span title={value}>{presentation}</span>;
  return <span className="text-muted-foreground" title={value}>—</span>;
}

export function buildColumnsFromProto(columns: Array<{ name: string; dataType?: string; data_type?: string; label?: string; refTable?: string; isRef?: boolean; format?: string }>, enumLabels?: Record<string, Record<string, string>>) {
  return columns.map((c) => {
    const dt = (c.dataType || c.data_type || '').toLowerCase();
    const isBool = dt === 'boolean';
    const isRef = (c.isRef === true) || ((dt === 'uuid' || dt === 'reference') && !!c.refTable);
    const isDate = dt === 'date' || dt === 'timestamp' || dt === 'datetime';
    // Перечисление: значение выводится СИНОНИМОМ (коды — только при недоступности
    // перечисления); SSoT значений — сервер (getEnum).
    const enumMap = dt === 'enumeration' ? enumLabels?.[c.name] : undefined;
    return {
      key: c.name,
      label: c.label || c.name,
      sortable: true,
      format: isBool ? 'boolean' as const : undefined,
      dataType: dt, // для карточек (ObjectCard форматирует по типу)
      importance: (c as any).importance,
      // ВАЖНО: isRef обязателен — карточки (ObjectCard) строят presentations
      // (серверные {col}_presentation) ТОЛЬКО для ссылочных колонок; без этого
      // флага ссылочные поля в карточках показывали сырой UUID вместо имени.
      isRef,
      render: isBool
        ? (val: unknown) => {
            if (val === null || val === undefined || val === '' || val === '<nil>') {
              return <Minus className="h-3.5 w-3.5 text-muted-foreground" />;
            }
            return val === true || val === 'true' || val === '1'
              ? <Check className="h-3.5 w-3.5 text-green-600" />
              : <X className="h-3.5 w-3.5 text-red-500" />;
          }
        : isRef
          ? (val: unknown, row?: Record<string, unknown>) => (
              <RefCell id={val} presentation={String((row || {})[`${c.name}_presentation`] ?? '')} />
            )
          : enumMap
            ? (val: unknown) => {
                const v = val === null || val === undefined ? '' : String(val);
                return enumMap[v] || v;
              }
            : (isDate || c.format)
              ? (val: unknown) => formatValue(val, c.format, dt)
              : undefined,
    };
  });
}

/**
 * Бейдж «обсуждение» (контекстный чат) в ТАБЛИЧНЫХ видах списков:
 * 💬N у имени (первая не-служебная колонка) — непрочитанные по контексту
 * (SSoT — contextChat). Декорация render'а первой колонки (детерминированно).
 */
export function decorateWithChatBadge(
  cols: Array<{ key: string; label: string; render?: (val: unknown, row?: Record<string, unknown>) => React.ReactNode; [k: string]: any }>,
  type: string,
  unreadFor: (t: string, id: string) => number,
) {
  const first = cols.find((c) => c.key !== '__tree');
  if (!first) return cols;
  const baseRender = first.render;
  first.render = (val: unknown, row?: Record<string, unknown>) => {
    const rowId = row && String(row.id) ? String(row.id) : '';
    const n = rowId ? unreadFor(type, rowId) : 0;
    return (
      <span className="inline-flex items-center gap-1.5 min-w-0">
        <span className="truncate min-w-0">{baseRender ? baseRender(val, row) : String(val ?? '')}</span>
        {n > 0 && (
          <span
            className="shrink-0 text-[9px] leading-none bg-primary text-primary-foreground rounded-full px-1.5 py-1"
            title="Непрочитанные в обсуждении"
          >
            💬{n}
          </span>
        )}
      </span>
    );
  };
  return cols;
}
